/**
 * Security Monitoring and Event Logging
 * Provides structured security event logging without exposing sensitive data
 */

export interface SecurityEvent {
  level: 'info' | 'warning' | 'error' | 'critical';
  event: string;
  service: string;
  timestamp: string;
  sessionId?: string;
  metadata?: Record<string, any>;
}

export class SecurityMonitor {
  private static readonly SESSION_ID = crypto.randomUUID().substring(0, 8);

  /**
   * Log a security event with structured data
   */
  static logEvent(
    level: SecurityEvent['level'],
    event: string,
    service: string,
    metadata: Record<string, any> = {}
  ): void {
    const securityEvent: SecurityEvent = {
      level,
      event,
      service,
      timestamp: new Date().toISOString(),
      sessionId: this.SESSION_ID,
      metadata: this.sanitizeMetadata(metadata)
    };

    // Development logging
    if (process.env.NODE_ENV === 'development') {
      const logMethod = level === 'critical' || level === 'error' ? 'error' : 
                       level === 'warning' ? 'warn' : 'info';
      console[logMethod]('[Security Monitor]', securityEvent);
    }

    // In production, integrate with monitoring services:
    // - Send to SIEM systems
    // - Alert on critical events
    // - Store in audit logs
    // - Monitor for attack patterns
  }

  /**
   * Log authentication-related security events
   */
  static logAuthEvent(event: string, metadata: Record<string, any> = {}): void {
    this.logEvent('info', event, 'Authentication', {
      ...metadata,
      userAgent: navigator.userAgent.substring(0, 100),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Log form submission security events
   */
  static logFormEvent(event: string, formType: string, metadata: Record<string, any> = {}): void {
    this.logEvent('warning', event, `Form:${formType}`, {
      ...metadata,
      timestamp: new Date().toISOString(),
      referrer: document.referrer ? new URL(document.referrer).hostname : 'direct'
    });
  }

  /**
   * Log critical security violations
   */
  static logSecurityViolation(event: string, service: string, metadata: Record<string, any> = {}): void {
    this.logEvent('critical', event, service, {
      ...metadata,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent.substring(0, 100)
    });
  }

  /**
   * Sanitize metadata to remove sensitive information
   */
  private static sanitizeMetadata(metadata: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(metadata)) {
      // Skip sensitive fields
      if (this.isSensitiveField(key)) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      // Truncate long strings
      if (typeof value === 'string' && value.length > 200) {
        sanitized[key] = value.substring(0, 200) + '...';
        continue;
      }

      // Keep safe values
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      } else {
        sanitized[key] = '[OBJECT]';
      }
    }

    return sanitized;
  }

  /**
   * Check if a field contains sensitive information
   */
  private static isSensitiveField(fieldName: string): boolean {
    const sensitiveFields = [
      'password', 'token', 'secret', 'key', 'auth',
      'email', 'phone', 'address', 'ssn', 'credit',
      'card', 'payment', 'personal'
    ];
    
    const lowerField = fieldName.toLowerCase();
    return sensitiveFields.some(sensitive => lowerField.includes(sensitive));
  }
}

/**
 * Rate limiting violation monitor
 */
export class RateLimitMonitor {
  private static violations = new Map<string, number>();

  static recordViolation(identifier: string, service: string): void {
    const current = this.violations.get(identifier) || 0;
    this.violations.set(identifier, current + 1);

    SecurityMonitor.logSecurityViolation('rate_limit_violation', service, {
      identifier: this.hashIdentifier(identifier),
      violationCount: current + 1,
      timestamp: new Date().toISOString()
    });

    // Alert on repeated violations
    if (current + 1 >= 5) {
      SecurityMonitor.logSecurityViolation('repeated_rate_limit_violations', service, {
        identifier: this.hashIdentifier(identifier),
        totalViolations: current + 1,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Hash identifier for privacy while maintaining ability to track patterns
   */
  private static hashIdentifier(identifier: string): string {
    // Simple hash for tracking without exposing actual identifier
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      const char = identifier.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `hash_${Math.abs(hash).toString(16)}`;
  }
}
